import { Component, OnInit, ViewChild } from '@angular/core';
import {Title} from "@angular/platform-browser";

import * as mapsData from 'devextreme/dist/js/vectormap-data/world.js';
import { ListsService } from '../services/lists.service';
import { Product } from '../models/product.model';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Watchlist } from '../models/watchlist.model';
import { ClustersService } from '../services/clusters.service';
import { Subscription } from 'rxjs';
import { NotificationsService } from '../services/notifications.service';

@Component({
  selector: 'app-database',
  templateUrl: './database.component.html',
  styleUrls: ['./database.component.scss']
})
export class DatabaseComponent implements OnInit {

  bestFitSelection: string = 'all';

  top18FactorIntensity: number;

  displayedColumns: string[] = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'factor_intensity'];
  dataSource: MatTableDataSource<Product>;

  productList: Product[] = [];
  selection: SelectionModel<Product>;
  emptySelection: boolean = true;
  databaseSelect: Array<any>;

  selectBlock: string = '';
  watchlistName: string = '';

  worldMap: any = mapsData.world;
  funds: Object;

  watchlists: Watchlist[];
  watchlistsSubscription: Subscription;
  watchlistsProducts: Product[][];
  selectedWatchlist: Watchlist;

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  constructor(private titleService: Title,
              private listsService: ListsService,
              private clustersService: ClustersService) {

    this.funds = {
      "United States": 4253,
      "Japan": 153,
      "Germany": 235,
      "France": 123,
      "United Kingdom": 526,
      "Italy": 13,
      "South Africa": 5,
      "Spain": 7,
      "Canada": 26
    };
    this.customizeLayers = this.customizeLayers.bind(this);
  }

  async ngOnInit() {
    await this.listsService.loadLists();

    this.productList = this.listsService.getProductList();
    this.determineTop18FactorIntensity();
    this.dataSource = new MatTableDataSource<Product>(this.productList);
    this.selection = new SelectionModel<Product>(true, []);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = (data: Product, filter: string) => {
      return this.isInBestFitSelection(data, filter) && (
        filter == 'no_filter' ||
        data.currency.alias.trim().toLowerCase() == filter ||
        data.name.trim().toLowerCase().includes(filter) ||
        data.investment_zone.name.trim().toLowerCase().includes(filter)
      );
    }

    this.subscribeToWatchlists();
  }

  ngOnDestroy() {
    if(this.watchlistsSubscription!=undefined) { this.watchlistsSubscription.unsubscribe(); }
  }

  subscribeToWatchlists() {
    this.watchlistsSubscription = this.clustersService.watchlistsSubject.subscribe(
      (watchlists: Watchlist[]) => {
        if (watchlists!=undefined && watchlists.length>0) {
          this.selectedWatchlist = watchlists[0];
          this.watchlists = watchlists;
        } else {
          this.watchlists = [];
        }
        this.loadWatchlistsProducts();
      }
    );
    this.clustersService.emitWatchlists();
  }

  loadWatchlistsProducts() {
    this.watchlistsProducts = this.watchlists.map((watchlist) => {
      return this.productList.filter((product) => {
        return watchlist.products_ids.includes(product.id);
      });
    });
  }

  getPageTitle() {
    return this.titleService.getTitle();
  }

  customizeTooltip(arg) {
    if (arg.attribute("fund")) {
      return {
        text: arg.attribute("name") + ": " + arg.attribute("fund") + " funds available"
      };
    }
  }

  customizeLayers(elements) {
    elements.forEach((element) => {
      element.attribute("fund", this.funds[element.attribute("name")]);
    });
  }

  determineTop18FactorIntensity() {
    this.top18FactorIntensity = this.productList.map((product: Product) => {
      return product.factor_intensity;
    }).sort((a, b) => b - a)[17];
  }

  bestFitSelectionChange() {
    this.dataSource.filter = 'no_filter';
  }

  bestFitViewChange(view: string) {
    switch (view) {
      case 'factor-intensity':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'factor_intensity'];
        break;
      case 'returns':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'returns'];
        break;
      case 'sharpe-ratio':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'sharpe_ratio'];
        break;
      case 'max-drawdown':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'maximum_drawdown'];
        break;
      case 'esg':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'esg'];
        break;
      default:
        break;
    }
  }

  onProductSelectionChange(event, row) {
    if (event) {
      this.selection.toggle(row);
    }
    this.emptySelection = this.selection.isEmpty();
  }

  isInBestFitSelection(product: Product, filter: string) {
    switch (this.bestFitSelection) {
      case 'top18':
        return product.factor_intensity>=this.top18FactorIntensity;
      case 'watchlists':
        return this.selectedWatchlist.products_ids.includes(product.id);
      case 'selected':
        return this.selection.isSelected(product);;
      case 'all':
        return true;
    }
  }

  applyFilter(event: Event) {
    let filterValue = (event.target as HTMLInputElement).value;
    if (filterValue=='' || this.bestFitSelection!='all') filterValue = 'no_filter';
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  async onWatchlistCreate() {
    let new_watchlist = {
      name: this.watchlistName,
      creation_date: undefined,
      nb_products: this.selection.selected.length,
      products_ids: this.selection.selected.map((product) => {
        return product.id;
      }),
    } as Watchlist;

    await this.clustersService.createWatchlist(new_watchlist);

    this.selectBlock = 'watchlists';
    this.bestFitSelection = 'all';
    this.selection.clear();
    this.watchlistName = '';
  }

  onDeleteWatchlist(watchlist_id: number) {
    this.clustersService.deleteWatchlist(watchlist_id);
  }
}
